import copy from "copy-to-clipboard";
import { useEffect, useState } from "react";

type UseClipboardReturn = [
  isCopied: boolean | undefined,
  onCopy: (copyValue?: string, copyId?: string) => void,
  copyId: string | undefined,
] & {
  isCopied: boolean | undefined;
  onCopy: (copyValue?: string, copyId?: string) => void;
  copyId: string | undefined;
};

/**
 * Hook to copy text to clipboard with notification timeout
 *
 * @param value - Default value to copy
 * @param notificationTimeout - Timeout in milliseconds for the copied notification
 * @default 2000
 */
const useClipboard = (
  value?: string,
  notificationTimeout = 2000,
): UseClipboardReturn => {
  const [isCopied, setCopied] = useState<boolean | undefined>();
  const [copyId, setCopyId] = useState<string | undefined>();

  const onCopy = (copyValue?: string, copyId?: string) => {
    const success = copy(copyValue || value || "");
    setCopied(success);
    if (typeof copyId !== "undefined") {
      setCopyId(copyId);
    }
  };

  useEffect(() => {
    let timeout: number;

    if (isCopied) {
      timeout = window.setTimeout(
        () => setCopied(undefined),
        notificationTimeout,
      );
    }

    return () => window.clearTimeout(timeout);
  }, [notificationTimeout, isCopied]);

  const ret = [isCopied, onCopy, copyId] as UseClipboardReturn;
  ret.isCopied = isCopied;
  ret.onCopy = onCopy;
  ret.copyId = copyId;

  return ret;
};

export default useClipboard;
